import IdentifyApi from '@apis/auth/IdentifyApi';
import userQueries from '@apis/queries/userQueries';
import UserApi from '@apis/user/UserApi';
import {
    ACCESS_TOKEN_MAX_AGE_SECOND,
    REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import useIdentification from '@hooks/auth/useIdentification';
import useIsEmailSignIn from '@hooks/auth/useIsEmailSignIn';
import useTermCategories from '@hooks/term/useTermCategories';
import useLoading from '@hooks/ui/useLoading';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { SignInResponse } from '@models/auth';
import { ErrorResponse } from '@models/error';
import { useFlow } from '@stackflow/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import SignUpContext from '../../../context/SignUpContext';

function useSignUp() {
    const { t } = useTranslation('signUp');
    const { pop } = useFlow();
    const {
        useVerified, useMarketing, useValue, kakaoId, appleId, nickname, useLocation, googleId,
        useSelectedTermCategories, verificationToken, inputEmail,
    } = useContext(SignUpContext);
    const project = useProject();
    const [verified] = useVerified;
    const [selectedTermCategories] = useSelectedTermCategories;
    const termCategories = useTermCategories('SIGN_UP');
    const signUpEnabled = useMemo(() => {
        if (!verified && !inputEmail && !project?.features.authenticationEnabled) {
            return false;
        }
        const requiredCategories = termCategories
            .filter(((tc) => tc.isRequired));
        return requiredCategories
            .every((rc) => selectedTermCategories.includes(rc.id));
    }, [verified, inputEmail, termCategories, selectedTermCategories, project]);
    const [marketingAgreed] = useMarketing;
    const [locationAgreed] = useLocation;
    const [value] = useValue;
    const projectId = useProjectId();
    const toast = useToast();
    const { startLoading, stopLoading } = useLoading();
    const [_, setCookie] = useCookies(['accessToken', 'refreshToken']);
    const queryClient = useQueryClient();
    const onSuccess = (data: SignInResponse) => {
        setCookie('accessToken', data.token, {
            path: '/',
            maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
        });
        setCookie('refreshToken', data.refreshToken, {
            path: '/',
            maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
        });
        queryClient.invalidateQueries({ queryKey: userQueries._def });
        pop(3);
    };
    const isEmail = useIsEmailSignIn();
    const { mutate } = useMutation({
        mutationKey: ['signUp', projectId, value, marketingAgreed, appleId, kakaoId, nickname],
        mutationFn: () => {
            if (isEmail) {
                return UserApi.signUpByEmail({
                    projectId,
                    marketingAgreed,
                    locationAgreed,
                    email: inputEmail ?? value,
                    appleId,
                    googleId,
                });
            }
            return UserApi.signUp({
                projectId,
                phoneNo: value,
                marketingAgreed,
                appleId,
                kakaoId,
                googleId,
                nickname,
                locationAgreed,
            });
        },
        onMutate() {
            startLoading();
        },
        onSuccess,
        onError(e: AxiosError<ErrorResponse>) {
            switch (e.response?.data.code) {
            case 'DUPLICATE_USER':
                toast.error(t('toast.duplicateUser'));
                break;
            case 'REJOIN_NOT_ALLOWED':
                toast.error(t('toast.rejoinNotAllowed'));
                break;
            default:
                toast.error(t('toast.unknownError'));
            }
            console.error(e);
        },
        onSettled() {
            stopLoading();
        },
    });
    const handleIdentification = useIdentification();

    const signUp = () => {
        if (project?.features.authenticationEnabled) {
            handleIdentification({
                projectId,
                marketingAgreed,
                appleId,
                kakaoId,
                nickname,
                locationAgreed,
                verificationToken,
            });
        } else {
            mutate();
        }
    };

    return {
        signUpEnabled,
        signUp,
    };
}

export default useSignUp;
